import { useLocale } from "hooks"
import React, { useCallback, useEffect, useState } from "react"
import type { VariantType } from "types"
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import type { CMSPageModule } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import type { PurchaseType } from "components/Shop/PDPPage/types"
import { navigateTo } from "utils/navigateTo"
import { useVariantIds } from "hooks/useVariantIds"
import type { AddBasketItemType } from "lib/api/addBasketItems"
import { addBasketItemsWithSpinner } from "lib/api/addBasketItems"
import { useRouter } from "lib/router"
import { useExpressCheckoutContext } from "components/Basket/hooks/useExpressCheckoutContext"
import type { ButtonVariantType } from "packages/ui-shared/components/Button/Button"
import { Button } from "packages/ui-shared/components/Button/Button"
import {
  addedCtaStep,
  memberFontSize,
  quickShopCta,
} from "components/ProductCardV2/QuickShop.css"
import { useButtonText } from "components/ProductCardV2/QuickShop"

interface Props {
  variant: VariantType
  buttonVariant: string
  pdpPurchaseType?: PurchaseType
  setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>
  setConfirmationVariant?: React.Dispatch<
    React.SetStateAction<VariantType | undefined>
  >
  title?: string | undefined
  locationType?: string
  locationTypeDetails?: string
  className?: string
  handleAddToBag?: (
    variant: VariantType,
    locationType?: string
  ) => Promise<void>
  trackAddToBagClick?: () => void
  quickShopButtonVariant?: ButtonVariantType
}

export const QuickShop: React.FC<Props> = ({
  variant,
  buttonVariant,
  quickShopButtonVariant = "tertiary",
  pdpPurchaseType = "member",
  setConfirmation,
  setConfirmationVariant,
  title = undefined,
  className,
  locationType,
  locationTypeDetails,
  handleAddToBag,
  trackAddToBagClick,
}) => {
  const router = useRouter()
  const locale = useLocale()
  const { pageId, quickShopTracking } = useLandingPageContext()
  const isLandingPage = !!pageId
  const [added, setAdded] = useState(false)
  const { giftMembershipProductId } = useVariantIds()
  const isGiftMembership = variant.id === giftMembershipProductId

  const buttonText = useButtonText(title, variant)

  const [buttonTitle, setButtonTitle] = useState(buttonText)

  const addedCta = added && buttonVariant === "atbMobile"

  useEffect(() => {
    setButtonTitle(buttonText)
  }, [buttonText])

  useEffect(() => {
    if (added) {
      setButtonTitle("Added!")
      setTimeout(() => {
        setButtonTitle(buttonText)
        setAdded(false)
      }, 1500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [added])

  useEffect(() => {
    setButtonTitle(buttonText)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonText, pdpPurchaseType])

  const onClickHandler = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (handleAddToBag) {
      e.stopPropagation()
      e.preventDefault()
      return handleAddToBag(variant, locationType)
    }

    if (isLandingPage) {
      e.preventDefault()
      cmsPageClick({
        ...quickShopTracking,
        moduleContentDetails: variant.sku,
      } as CMSPageModule)

      return addToBag()
    }

    e.preventDefault()

    return addToBag()
  }

  const { handleExpressCheckout } = useExpressCheckoutContext()

  const addToBag = useCallback(async () => {
    if (isGiftMembership) {
      await addBasketItemsWithSpinner(
        [
          {
            quantity: 1,
            variantId: giftMembershipProductId,
            variantSku:
              locale === "en-US" ? "BP_PLUS_GIFT_US" : "BP_PLUS_GIFT_UK",

            type: "product",
            origin: "user",
          },
        ],
        locationType,
        locationTypeDetails
      ).then(() => navigateTo("/basket"))
    } else {
      const items: AddBasketItemType[] = [
        {
          variantId: variant.id,
          quantity: 1,
          type: "product",
          origin: "user",
          variantSku: variant.sku,
        },
      ]

      await addBasketItemsWithSpinner(items, locationType, locationTypeDetails)
        .then(() => {
          if (setConfirmation && pdpPurchaseType && setConfirmationVariant) {
            setConfirmationVariant(variant)
            setConfirmation(true)
          }
          setAdded(true)
        })
        .catch(() => setAdded(false))

      // TODO: find a better solution for updating the express checkout state than this for the search results
      if (router.pathname === "/basket" && handleExpressCheckout) {
        handleExpressCheckout()
      }
    }
  }, [
    isGiftMembership,
    giftMembershipProductId,
    locale,
    locationType,
    locationTypeDetails,
    pdpPurchaseType,
    variant,
    router.pathname,
    handleExpressCheckout,
    setConfirmation,
    setConfirmationVariant,
  ])

  const disabled = variant.availability.status === "outofstock"

  const trackProductAddedToBag = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (trackAddToBagClick) {
      e.stopPropagation()
      trackAddToBagClick()
    }
  }
  const trackAddToBagCta = (e) => {
    if (!e) return
    onClickHandler(e)
    trackProductAddedToBag(e)
  }

  return (
    <Button
      className={`${className} ${quickShopCta} ${
        addedCta && addedCtaStep
      } ${memberFontSize}`}
      variant={quickShopButtonVariant}
      disabled={disabled}
      handleClick={(e) => {
        trackAddToBagCta(e)
      }}
    >
      {added ? "Added!" : buttonTitle}
    </Button>
  )
}
